import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';

@Component({
  standalone: false,
  selector: 'app-create-password',
  templateUrl: './create-password.component.html',
  styleUrls: ['./create-password.component.scss']
})
export class CreatePasswordComponent implements OnInit {
  public readonly appStoreUrl = 'https://apps.apple.com/us/app/fire-survey-pro/id6479811537';
  public readonly playStoreUrl = 'https://play.google.com/store/apps/details?id=surveyorpro.firesurveypro';
  public token: string;
  public email: string;
  public passwordForm: UntypedFormGroup;
  public error: any;
  public submitted = false;
  public completed = false;
  public canAccessApp = false;
  public invitation: {name: string, email: string, tenant_name: string | null} | null = null;
  private invitationUrl: string | null = null;

  constructor(
      private activatedRoute: ActivatedRoute,
      private router: Router,
      private fb: UntypedFormBuilder,
      private authService: AuthService,
      private snackBar: MatSnackBar,
  ) {
      if (this.authService.authenticated) {
          this.router.navigate(['/app']).then(r => r);
      }
  }

  get f() {
      return this.passwordForm.controls;
  }

  public ngOnInit(): void {
      this.passwordForm = this.fb.group({
          password: [null, Validators.required],
          password_confirmation: [null, Validators.required],
      });

      this.invitationUrl = this.resolveInvitationUrl();
      if (this.invitationUrl) {
          this.authService.getInvitation(this.invitationUrl).subscribe({
              next: invitation => this.invitation = invitation,
              // Older invitation links remain usable even if they predate the
              // signed details endpoint.
              error: () => this.invitation = null,
          });
      }
  }

  public onSubmit(): void {
      this.submitted = true;

      if (this.passwordForm.invalid) {
          return;
      }

      this.passwordForm.disable();

      this.authService.createPassword(this.invitationUrl, this.passwordForm.value).subscribe(
          (response: any) => {
              // Preserve the previous redirect if the API is deployed before
              // it starts returning platform access with the invitation response.
              if (response?.can_access_web !== false) {
                  this.snackBar.open('Your account is ready. You can now sign in.', 'Ok', {duration: 3000});
                  this.router.navigateByUrl('/login').then(r => r);

                  return;
              }

              this.completed = true;
              this.canAccessApp = response?.can_access_app === true;
          },
          (response: HttpErrorResponse) => {
              this.passwordForm.enable();
              this.snackBar.open(response?.error?.message, 'Ok', {duration: 5000});
          }
      );
  }

  private resolveInvitationUrl(): string | null {
      let url = this.activatedRoute.snapshot.queryParamMap.get('url');
      if (!url) return null;

      for (const parameter of ['expires', 'signature']) {
          const value = this.activatedRoute.snapshot.queryParamMap.get(parameter);
          if (value && !new RegExp(`[?&]${parameter}=`).test(url)) {
              url += `${url.includes('?') ? '&' : '?'}${parameter}=${encodeURIComponent(value)}`;
          }
      }

      return url;
  }
}
